import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import PersonIcon from '@material-ui/icons/Person';
import { Avatar } from '@material-ui/core';
import UserNameText from './UserNameText';

const letterToColor = (letter: string) => {
  switch (letter) {
    case 'A':
    case 'L':
    case 'W':
      return '#CF1322';
    case 'B':
    case 'M':
    case 'X':
      return '#722ED1';
    case 'C':
    case 'N':
    case 'Y':
      return '#1D39C4';
    case 'D':
    case 'O':
    case 'Z':
      return '#096DD9';
    case 'E':
    case 'P':
      return '#1890FF';
    case 'F':
    case 'Q':
      return '#00BCD4';
    case 'G':
    case 'R':
      return '#FA541C';
    case 'H':
    case 'S':
      return '#FA8C16';
    case 'I':
    case 'T':
      return '#009688';
    case 'J':
    case 'U':
      return '#4CAF50';
    case 'K':
    case 'V':
      return '#A2AAB3';
    default:
      return '#A2AAB3';
  }
};

const StyledAvatar = styled(Avatar)<{ $backgroundColor: string, $rightMargin: number }>`
  height: 32px;
  width: 32px;
  min-width: 32px;
  min-height: 32px;
  border-radius: 32px;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
  line-height: 32px;
  color: #FFF;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  display: flex;
  justify-content: center;
  margin-right: ${({ $rightMargin }) => $rightMargin}px;
`;

type SrcType = 'icon' | 'avatar';

const IconImage = styled.img<{ $srcType: SrcType }>`
  color: transparent;
  width: 100%;
  height: 100%;
  object-fit: cover;
  text-align: center;
  text-indent: 10000px;

  ${({ $srcType }) => $srcType === 'icon' && `
    padding: 7px;
  `}
`;

const StyledPersonIcon = styled(PersonIcon)`
  width: 100%;
  height: 100%;
  padding: 10%;
`;

export interface UserAvatarProps {
  name: string;
  src?: string;
  srcType?: SrcType;
  marginRight?: number;
  className?: string;
}

const UserAvatar: FC<UserAvatarProps> = ({
  name,
  className,
  src,
  srcType = 'icon',
  marginRight = 0,
}: UserAvatarProps) => {
  const nameInitial = name?.[0]?.toUpperCase() || '';
  const backgroundColor = letterToColor(nameInitial);

  const enableBGColor = useMemo(() => !src || srcType === 'icon', [src, srcType]);

  const child = useMemo(() => {
    if (src) return <IconImage src={src} alt={name} $srcType={srcType} />;
    return nameInitial ? <UserNameText>{nameInitial}</UserNameText> : <StyledPersonIcon />;
  }, [src, srcType, name, nameInitial]);

  return (
    <StyledAvatar
      $backgroundColor={enableBGColor && backgroundColor}
      $rightMargin={marginRight}
      className={className}
    >
      {child}
    </StyledAvatar>
  );
};

export default React.memo(UserAvatar);
